// Shortens the big figures in Ask AI's prose — "Rp 5,250,000,000" reads as
// "Rp 5.25B" — so an answer comparing a few valuations isn't a wall of
// digits. Thousands get K, millions M, billions B, trillions T. Display
// only: the stored answer keeps the exact figures, because the backend's
// figure verification and the history replayed to the model both depend
// on them, and the UI shows the exact value on hover.

// Below this a number is short enough to read as written.
const MIN_VALUE = 1_000;

const UNITS = [
  { value: 1_000_000_000_000, suffix: "T" },
  { value: 1_000_000_000, suffix: "B" },
  { value: 1_000_000, suffix: "M" },
  { value: 1_000, suffix: "K" },
];

// A comma-grouped figure ("1,250" or "1,250,000.5"), an Indonesian
// dot-grouped one with at least two groups ("1.250.000" — a single group
// like "1.250" may just be a decimal), or a bare run of 4+ digits. Not
// part of a larger token: a word, a dotted code like a NOP number, or a
// hyphenated identifier.
const NUMBER_RE =
  /(?<![\w.,]|\d-)(?:\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d{1,3}(?:\.\d{3}){2,}(?:,\d+)?|\d{4,}(?:\.\d+)?)(?![\w-]|[.,]\d)/g;

// A bare digit run is only treated as money when a currency sits right
// next to it; otherwise it may be a year, or a certificate, phone or
// reference number.
const CURRENCY_BEFORE_RE = /(?:Rp\.?|IDR)\s?$/i;
const CURRENCY_AFTER_RE = /^\s?IDR\b/i;

const parseFigure = (token) => {
  if (/^\d{1,3}(?:,\d{3})+/.test(token)) return Number(token.replace(/,/g, ""));
  if (/^\d{1,3}(?:\.\d{3}){2,}/.test(token)) return Number(token.replace(/\./g, "").replace(",", "."));
  return Number(token);
};

export const compactFigure = (value) => {
  let index = UNITS.findIndex((unit) => value >= unit.value);
  if (index === -1) return null;
  let amount = Math.round((value / UNITS[index].value) * 100) / 100;
  // 999,999,999 rounds to "1000M"; say "1B" instead.
  if (amount >= 1000 && index > 0) {
    index -= 1;
    amount = Math.round((value / UNITS[index].value) * 100) / 100;
  }
  return `${String(amount)}${UNITS[index].suffix}`;
};

// Splits text into plain strings and { text, full } figures, so the caller
// can render each figure with its exact value available.
export const compactNumberSegments = (text) => {
  if (!text) return [];
  const segments = [];
  let last = 0;
  for (const match of text.matchAll(NUMBER_RE)) {
    const token = match[0];
    const start = match.index;
    const end = start + token.length;
    const bare = /^\d+(?:\.\d+)?$/.test(token);
    if (bare) {
      const nearCurrency =
        CURRENCY_BEFORE_RE.test(text.slice(Math.max(0, start - 4), start)) ||
        CURRENCY_AFTER_RE.test(text.slice(end, end + 4));
      if (token.startsWith("0") || !nearCurrency) continue;
    }
    const value = parseFigure(token);
    if (!Number.isFinite(value) || value < MIN_VALUE) continue;
    if (start > last) segments.push(text.slice(last, start));
    segments.push({ text: compactFigure(value), full: token });
    last = end;
  }
  if (last < text.length) segments.push(text.slice(last));
  return segments;
};

export const compactNumbersInText = (text) =>
  compactNumberSegments(text)
    .map((segment) => (typeof segment === "string" ? segment : segment.text))
    .join("");
